import * as React from 'react';
import { StylesProps } from '../../../../theme/jss-types';
import { FormField } from '../../create-consignment.types';
import { formFields } from '../../create-modal.constants';
import { serviceTypeStyles } from './service-type.styles';
import {
    Radio,
    Col,
    Form,
    FormInstance,
    Row,
} from 'antd';
import { Service } from '../../../../library/Types';
import Loader from '../../../common/Loader';
import { getBusinessTat, getInternationalBusinessTatV2 } from '../../../../network/common.api';
import { useTranslation } from 'react-i18next';
import Helper from 'library/Helper';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import { ReduxStore } from 'reducers/redux.types';
import { CustomerPortalConfig } from 'types/master-data-types';
import { formRules } from 'library/constants';

const {
    useState,
    useEffect,
} = React;
interface IProps extends StylesProps<ReturnType<typeof serviceTypeStyles>> {
    form: FormInstance;
    params: any;
    allowedConsignmentFormType: any;
}

const ServiceTypeSelectorField = (props: IProps) => {
    const {
        classes,
        form,
        params,
        allowedConsignmentFormType,
    } = props;
    const {
        Services,
    } = formFields;
    const { t } = useTranslation();
    const [selectedService, setSelected] = useState<string>();
    const [loading, setLoading] = useState<boolean>(true);
    const [services, setServices] = React.useState<any[]>([]);
    const isEditingCN = Form.useWatch('isEditingCN', form);
    const { isCurrentTab, getValidationRules, disabledFields = {} } = params;
    const isServiceDisabled = !!disabledFields[Services.key];

    const loadServices = async () => {
        setLoading(true);
        const storage = window.localStorage;
        const formData = form.getFieldsValue();

        const commonParts = {
            srcPincode: formData.srcAddress?.pincode,
            srcStateName: formData.srcAddress?.stateName,
            dstStateName: formData.dstAddress?.stateName,
            dstPincode: formData.dstAddress?.pincode,
            countryName: formData.dstAddress?.countryName,
            clientCode: storage.getItem('userCode'),
            width: formData.width,
            hight: formData.hight,
            length: formData.length,
            weight: formData.weight || 1,
            numPieces: formData?.numberOfPieces,
            referenceNumber: formData.referenceNumber,
            courierType: formData.courierType?.toUpperCase(),
            is_international: formData.is_international,
            pieceDetails: formData?.courierType === 'non-document'
                ? [{
                    length: Helper.converttoCentimeter(formData?.length, formData?.unit),
                    width: Helper.converttoCentimeter(formData?.width, formData?.unit),
                    height: Helper.converttoCentimeter(formData?.height, formData?.unit),
                }] : undefined,
        };
        if (formData.consignmentCategory === 'international') {
            let commodityCode;
            formData?.contentList?.forEach((commodity: any) => {
                if (formData?.commodityId && formData?.commodityId[0] === commodity?.COMMODITY_ID) {
                    commodityCode = commodity?.COMMODITY_CODE;
                }
            });
            const response = await getInternationalBusinessTatV2({
                commodityCode,
                dstCountry: formData?.dstAddress?.countryName,
                dstCity: formData?.dstAddress?.cityName,
                srcCity: formData?.srcAddress?.cityName,
                ...commonParts,
            });
            setServices(response?.data?.map((service: any) => {
                return {
                    ...service,
                    serviceType: service.serviceType,
                    serviceTypeId: service.serviceType,
                };
            }) || []);
        } else {
            const response = await getBusinessTat(commonParts);
            setServices(response?.data || []);
        }
        setLoading(false);
    };

    const prefillData = () => {
        setSelected(form.getFieldValue(Services.key));
    };

    useEffect(() => {
        if (isCurrentTab) {
            loadServices();
            prefillData();
        }
    }, [isCurrentTab]);

    const renderCustomError = () => {
        const errors = form.getFieldError(Services.key);
        if (errors && Array.isArray(errors) && errors.length > 0) {
            return (
                <div style={{
                    color: '#ff4d4f',
                    fontSize: 12,
                    textAlign: 'center',
                }}
                >
                    {errors[0]}
                </div>
            );
        }
        return null;
    };

    const renderTitle = (field: FormField) => {
        return (
            <Row className={classes.referenceTitle}>
                {t(field.key)}
                {renderCustomError()}
            </Row>
        );
    };

    const handleSelection = (item: Service) => {
        if (isServiceDisabled) return;
        if (selectedService === item.serviceTypeId) {
            setSelected(undefined);
            form.setFieldsValue({
                [Services.key]: undefined,
            });
        } else {
            setSelected(item.serviceTypeId);
            form.setFieldsValue({
                [Services.key]: item.serviceTypeId,
            });
        }
    };

    const getRadioClass = (item: Service) => {
        const classList = [classes.radio];
        if (selectedService === item.serviceTypeId) {
            classList.push(classes.radioSelected);
        }
        return classList.join(' ');
    };

    const renderServiceName = (item: Service) => {
        return (
            <div className={classes.serviceItem}>
                {item.serviceType}
            </div>
        );
    };

    const renderTat = (item: Service) => {
        const tat = item?.period || item?.tat || item?.TAT;
        if (!tat) {
            return <div className={classes.tat} />;
        }
        return (
            <div className={classes.tat}>
                {tat.toString().endsWith('Days') ? tat : `${tat} Days`}
            </div>
        );
    };

    const renderRate = (item: Service) => {
        if (!item.rate?.amount) {
            return <div className={classes.finalPrice} />;
        }
        return (
            <div className={classes.finalPrice}>
                {item.rate?.currencySymbol ? `${item.rate.currencySymbol} ` : ''}
                {`${item.rate?.amount}`}
            </div>
        );
    };

    const renderDeliveryTime = (item: Service) => {
        if (!item.deliveryTime) {
            return <div className={classes.finalPrice} />;
        }
        return (
            <span className={classes.finalPrice}>
                {item.deliveryTime}
            </span>
        );
    };

    const renderService = (item: Service) => {
        return (
            <Row className={getRadioClass(item)} onClick={() => handleSelection(item)}>
                <Col span={12}>
                    <Radio
                        name={item.serviceTypeId}
                        key={item.serviceTypeId}
                        checked={item.serviceTypeId === selectedService}
                        disabled={isEditingCN || isServiceDisabled}
                    >
                        {renderServiceName(item)}
                    </Radio>
                </Col>

                {
                    allowedConsignmentFormType.includes('Rate')
                        ? <Col span={4} className={classes.column}>{renderRate(item)}</Col>
                        : null
                }
                {
                    allowedConsignmentFormType.includes('TAT')
                        ? <Col span={4} className={classes.column}>{renderTat(item)}</Col>
                        : null
                }
                {
                    allowedConsignmentFormType.includes('Delivery Time')
                        ? <Col span={4} className={classes.column}>{renderDeliveryTime(item)}</Col>
                        : null
                }
            </Row>
        );
    };

    const formRuleRequired = {
        ...formRules.required,
        message: t('service_type_required'),
    };

    const renderRadio = (field: FormField) => {
        return (
            <Row>
                <Form.Item
                    name={field.key}
                    initialValue={field.defaultValue}
                    valuePropName={field.valuePropName}
                    className={classes.serviceListContainer}
                    rules={[formRuleRequired, ...getValidationRules(field)]}
                >
                    {services?.map((item) => renderService(item))}
                </Form.Item>
            </Row>
        );
    };

    const renderLine = () => {
        return (
            <Row className={classes.line} />
        );
    };

    const renderHeaders = () => {
        return (
            <Row className={classes.headerRow}>
                <Col span={10} offset={2} className={classes.headerCell}>
                    {t('service_type')}
                </Col>
                {allowedConsignmentFormType.includes('Rate') && (
                    <Col span={4} className={classes.headerCell}>
                        {t('rate')}
                    </Col>
                )}
                {allowedConsignmentFormType.includes('TAT') && (
                    <Col span={4} className={classes.headerCell}>
                        {t('TAT')}
                    </Col>
                )}
                {allowedConsignmentFormType.includes('Delivery Time') && (
                    <Col span={4} className={classes.headerCell}>
                        {t('delivery_time')}
                    </Col>
                )}
            </Row>
        );
    };

    const renderServicesForm = () => {
        return (
            <Row className={classes.container}>
                {renderTitle(Services)}
                {renderLine()}
                {renderHeaders()}
                {renderRadio(Services)}
            </Row>
        );
    };

    return (
        <div style={{ width: '100%' }}>
            {loading && <Loader zIndex={10} />}
            <div className={classes.box}>
                {renderServicesForm()}
            </div>
        </div>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    const customerPortalConfig:CustomerPortalConfig = state?.master?.config?.customer_portal_config;
    const allowedConsignmentFormType = state?.master?.config?.customer_portal_ops_config
        ?.consignment_flow_type_config?.config_value?.allowed_consignment_form_type_customer_portal || [];
    return {
        allowedConsignmentFormType,
        showRiskSurchargeType: customerPortalConfig?.show_risk_surcharge_type,
    };
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: serviceTypeStyles,
    },
    connectRouter: true,
    connectTranslession: true,
};

export default GenericHoc(hocConfig)(ServiceTypeSelectorField);
